import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMergeRefs, useOptionallyControlledState } from "../../../hooks";
import { useNumberFieldInputMode } from "./internal/useNumberFieldInputMode";
import { useMaskito } from "@maskito/react";
import {
  MaskitoNumberParams,
  maskitoParseNumber,
  maskitoNumberOptionsGenerator,
  maskitoStringifyNumber,
} from "@maskito/kit";
import useNumberFieldKeys from "./internal/useNumberFieldKeys";
import Big from "big.js";
import { usePrevious } from "..";

const DEFAULT_MIN_VALUE = 0;
// This value is somewhat arbitrary, but it's a large enough value that it shouldn't present any practical issues.
// If we need to support larger values, we can address that as the need arises.
const DEFAULT_MAX_VALUE = 100_000_000_000_000;

/**
 * Configuration options for the useNumberField hook
 */
export type UseNumberFieldConfig = {
  /**
   * The label for the number field.
   * Important for accessibility and button labeling.
   */
  label?: string;
  /**
   * The minimum value for the number field.
   * @default 0
   */
  minValue?: number;
  /**
   * The maximum value for the number field.
   * @default 100000000000000
   */
  maxValue?: number;
  /**
   * The step value for the number field.
   * @default 1
   */
  step?: number;
  /**
   * The controlled value for the number field.
   */
  value?: number | null;
  /**
   * The default (uncontrolled) value for the number field.
   */
  defaultValue?: number | null;
  /**
   * The maximum number of decimal places for the number field.
   * @default 0
   */
  maximumFractionDigits?: number;
  /**
   * The minimum number of decimal places for the number field.
   * @default 0
   */
  minimumFractionDigits?: number;
  /**
   * A callback that is called when the value changes.
   * @param value The new numeric value or null if invalid/empty
   */
  onChange?: (value: number | null) => void;
};

/**
 * Return value from the useNumberField hook
 */
export type UseNumberFieldReturnValue = {
  /** The current numeric value of the number field. */
  value: number | null;
  /** A ref that should be attached to the input element. */
  inputRef: React.RefObject<HTMLInputElement>;
  /** The props to pass to the input element. */
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  /** The props to pass to the increment button. */
  incrementButtonProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /** The props to pass to the decrement button. */
  decrementButtonProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
};

/**
 * Custom hook that provides the functionality for number input fields with increment and decrement controls.
 *
 * This hook handles:
 * - Precise decimal arithmetic using Big.js
 * - Number formatting with thousand separators and decimal precision
 * - Input validation and value clamping
 * - Keyboard navigation (arrows, PageUp/Down, Home/End)
 * - Platform-specific input modes for mobile devices
 * - Accessibility attributes and button states
 * - Controlled and uncontrolled value management
 *
 * @param config - Configuration options for the number field
 * @returns Object containing the current value, input ref, and props for input and buttons
 *
 * @example
 * const { value, inputRef, inputProps, incrementButtonProps, decrementButtonProps } = useNumberField({
 *   label: "Quantity",
 *   minValue: 0,
 *   maxValue: 100,
 *   step: 1,
 *   onChange: (value) => console.log(value)
 * });
 */
export const useNumberField = (config: UseNumberFieldConfig) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    minValue = DEFAULT_MIN_VALUE,
    maxValue = DEFAULT_MAX_VALUE,
    label = "",
  } = config;

  const step = config.step ? Math.abs(config.step) : 1;

  const minimumFractionDigits = Math.abs(config.minimumFractionDigits ?? 0);
  // In a situation where the maximum fraction digits is less than the minimum fraction digits,
  // the minimum fraction digits takes precedence over the maximum fraction digits.
  const maximumFractionDigits = Math.max(
    Math.abs(config.maximumFractionDigits ?? 0),
    minimumFractionDigits,
  );

  const previousMinimumFractionDigits = usePrevious(
    minimumFractionDigits,
    minimumFractionDigits,
  );

  const params: MaskitoNumberParams = useMemo(
    () => ({
      decimalSeparator: ".",
      thousandSeparator: ",",
      minusSign: "-",
      minimumFractionDigits,
      maximumFractionDigits,
    }),
    [minimumFractionDigits, maximumFractionDigits],
  );
  const options = maskitoNumberOptionsGenerator(params);

  const [inputValue, setInputValue] = useOptionallyControlledState<
    string | null
  >({
    controlledValue: convertValueProp(config.value, { maskitoParams: params }),
    defaultValue:
      convertValueProp(config.defaultValue, { maskitoParams: params }) ?? null,
    onChange: (value) => {
      if (value === null) return config.onChange?.(null);
      const parsedValue = maskitoParseNumber(value, params);
      const nanSafeValue = isNaN(parsedValue) ? null : parsedValue;
      config.onChange?.(nanSafeValue);
    },
  });

  useEffect(() => {
    if (previousMinimumFractionDigits === minimumFractionDigits) return;
    // If minimumFractionDigits changes, we need to update the input state.
    // E.g. if the user changes the minimumFractionDigits from 0 to 2, and the value is 10 we need to update the input state to 10.00.
    setInputValue((previous) => {
      if (previous === null || previous === undefined) return null;
      const parsedValue = maskitoParseNumber(previous, params);
      const nanSafeValue = isNaN(parsedValue) ? 0 : parsedValue;
      return maskitoStringifyNumber(nanSafeValue, params);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumFractionDigits, previousMinimumFractionDigits]);

  const maskedInputRef = useMaskito({
    options,
  });

  const computedValue: Big | null = useMemo(() => {
    if (inputValue === null) return null;
    const parsedValue = maskitoParseNumber(inputValue, params);
    if (isNaN(parsedValue)) {
      return null;
    }
    const bigValue = new Big(parsedValue);
    return bigValue;
  }, [inputValue, params]);

  const clamp = useCallback(
    (value: number) => {
      return Math.min(Math.max(value, minValue), maxValue);
    },
    [minValue, maxValue],
  );

  const increment = useCallback(
    (amount: number) => {
      setInputValue((prevValue) => {
        const parsedValue = maskitoParseNumber(prevValue ?? "0", params);
        const nanSafeValue = isNaN(parsedValue) ? 0 : parsedValue;
        const incrementedValue = new Big(nanSafeValue).plus(amount);
        const clampedValue = clamp(incrementedValue.toNumber());
        return maskitoStringifyNumber(clampedValue, params);
      });
    },
    [clamp, params, setInputValue],
  );

  const decrement = useCallback(
    (amount: number) => {
      setInputValue((prevValue) => {
        const parsedValue = maskitoParseNumber(prevValue ?? "0", params);
        const nanSafeValue = isNaN(parsedValue) ? 0 : parsedValue;
        const decrementedValue = new Big(nanSafeValue).minus(amount);
        const clampedValue = clamp(decrementedValue.toNumber());
        return maskitoStringifyNumber(clampedValue, params);
      });
    },
    [clamp, params, setInputValue],
  );

  const setToMin = useCallback(() => {
    setInputValue(maskitoStringifyNumber(minValue, params));
  }, [minValue, params, setInputValue]);

  const setToMax = useCallback(() => {
    setInputValue(maskitoStringifyNumber(maxValue, params));
  }, [maxValue, params, setInputValue]);

  const { inputRef: keysInputRef } = useNumberFieldKeys({
    increment,
    decrement,
    setToMin,
    setToMax,
    step,
    largeStepMultiplier: 10,
  });

  const combinedInputRef = useMergeRefs([
    inputRef,
    maskedInputRef,
    keysInputRef,
  ]);

  const hasDecimals = minimumFractionDigits > 0 || maximumFractionDigits > 0;
  const hasNegative = minValue < 0;

  const inputMode = useNumberFieldInputMode({
    hasDecimals,
    hasNegative,
  });

  const pattern = useMemo(() => {
    if (hasNegative) {
      return `-?[0-9]*(.[0-9]{0,${maximumFractionDigits}})?`;
    }
    if (hasDecimals) {
      return `[0-9]*(.[0-9]{0,${maximumFractionDigits}})?`;
    }
    return "[0-9]*";
  }, [maximumFractionDigits, hasDecimals, hasNegative]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "") {
      setInputValue(null);
    } else {
      setInputValue(e.target.value);
    }
  };

  return {
    value: computedValue?.toNumber() ?? null,
    inputRef: combinedInputRef,
    inputProps: {
      value: inputValue ?? "",
      onChange: handleInputChange,
      inputMode,
      "aria-valuenow": computedValue?.toNumber() ?? undefined,
      "aria-valuemin": minValue,
      "aria-valuemax": maxValue,
      min: minValue,
      max: maxValue,
      step,
      role: "spinbutton",
      pattern,
      type: "text",
      autoComplete: "off",
    },
    incrementButtonProps: {
      onClick: () => increment(step),
      disabled: computedValue?.gte(maxValue),
      tabIndex: -1,
      "aria-label": `Increment ${label ?? ""}`,
    },
    decrementButtonProps: {
      onClick: () => decrement(step),
      disabled: computedValue?.lte(minValue),
      tabIndex: -1,
      "aria-label": `Decrement ${label ?? ""}`,
    },
  };
};

/**
 * Converts a numeric value to a formatted string using Maskito parameters.
 *
 * @param v - The numeric value to convert
 * @param maskitoParams - Maskito configuration parameters for formatting
 * @returns Formatted string representation of the value, or null/undefined if input is null/undefined
 */
function convertValueProp(
  v: number | null | undefined,
  { maskitoParams }: { maskitoParams: MaskitoNumberParams },
): string | null | undefined {
  if (v === undefined || v === null) {
    return v;
  }
  let bigValue: Big;
  try {
    bigValue = new Big(v);
  } catch (_) {
    bigValue = new Big(0);
  }
  return maskitoStringifyNumber(
    bigValue.round(maskitoParams.maximumFractionDigits).toNumber(),
    maskitoParams,
  );
}
